import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { BusinessDay } from '@cemex-core/cmx-api/models/calendars/calendar';
import {
  getCutOff,
  getIsTemperatureControl,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/cut-off.selectors';
import { distinctUntilChanged, filter, map, pairwise, switchMap, withLatestFrom } from 'rxjs/operators';
import * as moment from 'moment/moment';
import {
  getActiveCountryCode,
  getActiveExperienceCode,
  getActiveExperienceCodeAndCountryCode,
  getLocationPage,
  getRmsSpecificationFormValues,
  getStepperState,
} from '@ordersnproducts/modules/order-builder/reducers';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { isEqual } from 'lodash';
import { getOpenPlatformContracts } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/contracts.selectors';
import { getDcoState } from '@ordersnproducts/modules/shared/reducers';
import { ContractOP, COUNTRY_CODE } from '@cemex-core/cmx-api';
import { TranslateService } from '@ngx-translate/core';
import { CwcAlert } from '@cmx-web-components/angular';
import { ObservableValue } from '@ordersnproducts/utils/models';
import { ORDER_EXPERIENCES } from '@ordersnproducts/modules/shared/constants/order-experiences';
import { getCurrentLanguageWithLocaleInfo } from '@ordersnproducts/utils/utils';
import { Features } from '@ordersnproducts/features';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { ExperiencesService } from '@ordersnproducts/modules/shared/services/experiences.service';
import { Moment } from 'moment';

/**
 * Find a contract using a product code and a contract code.
 * @param productContracts a dict of contract arrays indexed by product codes
 * @param productCode
 * @param contractCode
 */
const findContract = (
  productContracts: Record<string, ContractOP[]>,
  productCode: string,
  contractCode: string,
): ContractOP | null => {
  const contractsForSelectedProduct: ContractOP[] = productContracts[productCode] || [];
  return (
    contractsForSelectedProduct.find((contract) => contract.contractCode.replace(/^0+/, '') === contractCode) || null
  );
};

const getOfferAndBusinessDay = (
  store: Store<any>,
  enabledBusinessDays: boolean,
): Observable<{ offerDays: number; businessDays: BusinessDay[] | null }> => {
  return store.select(getActiveExperienceCodeAndCountryCode).pipe(
    switchMap(({ experienceCode, countryCode }) => {
      return store.select(getLocationPage).pipe(
        filter((locationState) => {
          return locationState != null;
        }),
        map((locationState) => {
          let offerDays: number;
          let businessDays: BusinessDay[] = null;
          if (locationState.calendars != null && locationState.calendars.calendars.length > 0) {
            if (enabledBusinessDays && experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK) {
              businessDays = locationState.calendars.calendars[0].businessDays;
              offerDays = locationState.calendars.calendars[0].offerDays - 1;
            } else {
              offerDays = locationState.calendars.calendars[0].offerDays - 1;
            }
          } else {
            offerDays = 30 - 1;
          }
          return { offerDays, businessDays };
        }),
      );
    }),
  );
};

/**
 * Creates and observable to get from the store the cut off value and the offer days value.
 * @returns An Observable with the cut off value as string and offer days as a number.
 */
const getCutOffAndOfferDays = (
  store: Store<any>,
  enabledBusinessDays: boolean,
): Observable<{ cutOffString: string; offerDays: number; businessDays?: BusinessDay[] | null }> => {
  return store.select(getCutOff).pipe(
    withLatestFrom(getOfferAndBusinessDay(store, enabledBusinessDays)),
    map(([cutOff, { offerDays, businessDays }]) => {
      let cutOffString = moment().format('YYYY-MM-DD');
      if (cutOff != null) {
        const time = cutOff.timeCut.replace('.000Z', '');
        cutOffString = cutOff.dateAvailable + 'T' + time;
      }
      return { cutOffString, offerDays, businessDays };
    }),
  );
};

/**
 * Calculate the maximum available offered date for order delivery.
 * @param offerDays
 * @param cutOffString
 * @param businessDays
 * @param experienceCode
 * @param enabledBusinessDays
 */
export const calculateMaxOfferDateTimeMoment = ({
  offerDays,
  cutOffString,
  businessDays,
  experienceCode,
  enabledBusinessDays,
}: {
  offerDays: number;
  cutOffString: string;
  businessDays: BusinessDay[];
  experienceCode: string;
  enabledBusinessDays: boolean;
}) => {
  if (
    experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK &&
    enabledBusinessDays &&
    businessDays &&
    businessDays.length > 0
  ) {
    const maxBusinessDay = businessDays[businessDays.length - 1].date;
    return moment(maxBusinessDay);
  } else {
    return moment(cutOffString).add(offerDays, 'days');
  }
};

@Injectable({
  providedIn: 'root',
})
export class ContractsExpirationService {
  public contractExpirationAlertProps$: Observable<
    Partial<Pick<CwcAlert, 'keepOpen' | 'show' | 'message'>> | undefined | null
  >;
  public multipleMixesAlertProps$: Observable<
    Partial<Pick<CwcAlert, 'keepOpen' | 'show' | 'message'>> | undefined | null
  >;

  /**
   * Maximum available date and time for order delivery.
   */
  public maxDateTimeMoment$: Observable<Moment>;

  /**
   * Info used by the dco-component to limit delivery time window based on contract expiration date.
   */
  public deliveryDateInfo$: Observable<{
    /**
     * Maximum available date and time for order delivery.
     *
     * @deprecated use maxDate calculations provided by the dco-component instead.
     * TODO remove property and it's related code
     */
    maxDateTimeMoment: Moment;
    /**
     * The expiration date of the currently selected contract.
     */
    expirationContractMoment: Moment;
  }>;

  constructor(
    store: Store<object>,
    private translate: TranslateService,
    featureToggle: FeatureToggleService,
    experiencesService: ExperiencesService,
  ) {
    const enabledBusinessDays = featureToggle.isFeatureEnabled(Features.EnabledBusinessDays);
    const enabledContractsExpirationDate = featureToggle.isFeatureEnabled(Features.ContractsExpirationDate);
    const removeContractToggle = featureToggle.isFeatureEnabled(Features.RemoveContractFieldEnabled);

    this.maxDateTimeMoment$ = getCutOffAndOfferDays(store, enabledBusinessDays).pipe(
      withLatestFrom(store.select(getActiveExperienceCode)),
      filter(([cutOffAndOfferDays, experienceCode]) => {
        return cutOffAndOfferDays !== null && experienceCode !== null;
      }),
      map(([{ offerDays, cutOffString, businessDays }, experienceCode]) =>
        calculateMaxOfferDateTimeMoment({
          offerDays,
          cutOffString,
          businessDays,
          experienceCode,
          enabledBusinessDays,
        }),
      ),
    );
    const commandDcoEnabled$ = experiencesService.commandDcoEnabled$();

    const mappedSpecificationFormValues$ = store.select(getRmsSpecificationFormValues).pipe(
      filter((values) => !!values && values.length > 0),
      map((values) => values[0]),
      filter((form) => !!form && !!form.values),
      map((form) => ({
        contractSelect: (form.values[SpecFieldNames.CONTRACT_SELECT] as string) || null,
        productSelect: (form.values[SpecFieldNames.PRODUCT_SELECTION] as string) || null,
        temperatureUnder: (form.values[SpecFieldNames.TEMPERATURE_UNDER] as string) || null,
        iceQuantity: (form.values[SpecFieldNames.ICE_QUANTITY] as string) || null,
        selectedDate: (form.values.selectedDate as string) || null,
        id: form.id,
      })),
      distinctUntilChanged((prev, next) => isEqual(prev, next)),
      switchMap((values) => {
        return store.select(getOpenPlatformContracts).pipe(
          map((contracts) => {
            return {
              ...values,
              selectedContract: findContract(contracts, values.productSelect, values.contractSelect),
            };
          }),
        );
      }),
      switchMap((values) => {
        return store.select(getDcoState).pipe(
          map((dcoState) => {
            return {
              ...values,
              selectedDate: !!dcoState.entities[values.id] ? dcoState.entities[values.id].requestDatetime : null,
            };
          }),
        );
      }),
    );

    const maxOfferDateTimeMomentAndCountryCode$ = this.maxDateTimeMoment$.pipe(
      withLatestFrom(store.select(getActiveCountryCode)),
      filter(([maxDateTimeMoment, countryCode]) => {
        return maxDateTimeMoment !== null && countryCode !== null;
      }),
      map(([maxDateTimeMoment, countryCode]) => ({
        countryCode,
        maxDateTimeMoment,
      })),
    );

    const localeDateFormat$ = store.select(getCurrentLanguageWithLocaleInfo).pipe(
      filter((language) => language != null),
      map((language) => {
        return { formatDate: language.formatDate };
      }),
    );

    const stepperActiveStep$ = store.select(getStepperState).pipe(
      filter((stepperState) => stepperState != null),
      map((stepperState) => {
        return { activeStep: stepperState.activeStep };
      }),
    );

    const formValuesNewModifyOrder$ = combineLatest([
      mappedSpecificationFormValues$,
      maxOfferDateTimeMomentAndCountryCode$,
      localeDateFormat$,
      stepperActiveStep$,
      commandDcoEnabled$,
      store.select(getIsTemperatureControl),
    ]).pipe(
      map(
        ([
          mappedSpecificationFormValues,
          maxOfferDateTimeMomentAndCountryCode,
          localeDateFormat,
          stepperActiveStep,
          commandDcoEnabled,
          isTemperatureControl,
        ]) => ({
          ...mappedSpecificationFormValues,
          ...maxOfferDateTimeMomentAndCountryCode,
          ...localeDateFormat,
          ...stepperActiveStep,
          commandDcoEnabled,
          isTemperatureControl,
        }),
      ),
      filter(
        ({ countryCode, commandDcoEnabled }) =>
          (countryCode === COUNTRY_CODE.USA && commandDcoEnabled) || countryCode === COUNTRY_CODE.UNITED_KINGDOM,
      ),
      map((values) => {
        return {
          ...values,
          selectedDateMoment: values.selectedDate ? moment(values.selectedDate).startOf('day') : null,
          expirationContractMoment:
            values.selectedContract && values.selectedContract.validOnDateTo
              ? moment(values.selectedContract.validOnDateTo).startOf('day')
              : null,
        };
      }),
      distinctUntilChanged((prev, next) => isEqual(prev, next)),
      pairwise(),
      map(([pastValue, newValue]) => {
        if (
          pastValue.contractSelect === newValue.contractSelect &&
          pastValue.productSelect === newValue.productSelect
        ) {
          pastValue.contractSelect = null;
        }
        return [pastValue, newValue];
      }),
      filter(([pastValue, newValue]) => {
        return (
          pastValue.contractSelect !== newValue.contractSelect || pastValue.productSelect !== newValue.productSelect
        );
      }),
    );

    const observableShowMultipleMixesAlert$ = combineLatest([
      store.select(getRmsSpecificationFormValues).pipe(
        filter((values) => !!values && values.length > 0),
        map((values) => values.length),
      ),
      store.select(getStepperState).pipe(
        map((stepperState) => {
          return stepperState.activeStep === 2;
        }),
      ),
    ]).pipe(
      map(([rmsSpecificationFormValuesLength, stepperAtSpecificationStep]) => ({
        rmsSpecificationFormValuesLength,
        stepperAtSpecificationStep,
      })),
    );

    const alerts$ = combineLatest([
      formValuesNewModifyOrder$,
      observableShowMultipleMixesAlert$,
      store.select(getActiveExperienceCode),
    ]).pipe(
      map(([orderCreationFormValues, multipleMixesAlertData, experienceCode]) =>
        this.calcFormAlertActivation(
          orderCreationFormValues,
          multipleMixesAlertData,
          enabledContractsExpirationDate,
          experienceCode,
          removeContractToggle,
        ),
      ),
    );

    this.contractExpirationAlertProps$ = alerts$.pipe(
      map(({ contractExpirationAlertProps }) => contractExpirationAlertProps),
    );
    this.multipleMixesAlertProps$ = alerts$.pipe(map(({ multipleMixesAlertProps }) => multipleMixesAlertProps));

    this.deliveryDateInfo$ = formValuesNewModifyOrder$.pipe(
      map(([, next]) =>
        enabledContractsExpirationDate
          ? {
              maxDateTimeMoment: next.maxDateTimeMoment,
              expirationContractMoment: next.expirationContractMoment,
            }
          : null,
      ),
    );
  }

  /**
   * Calculate which alerts to display based on various form data.
   *
   * Essentially calculates props for CwcAlert components, which can then be passed to the respective inputs.
   *
   * @param orderCreationFormValues
   * @param multipleMixesAlertData
   * @param contractsExpirationFeatureEnabled
   * @private
   */
  private calcFormAlertActivation(
    orderCreationFormValues: {
      expirationContractMoment: Moment | null;
      formatDate: string | null;
      activeStep: number | null;
      contractSelect: string | null;
      maxDateTimeMoment: Moment | null;
      selectedDateMoment: Moment | null;
    }[],
    multipleMixesAlertData: { rmsSpecificationFormValuesLength: number; stepperAtSpecificationStep: boolean } | null,
    contractsExpirationFeatureEnabled: boolean,
    experienceCode: string | null,
    removeContractToggle: boolean,
  ): {
    contractExpirationAlertProps?: ObservableValue<ContractsExpirationService['contractExpirationAlertProps$']>;
    multipleMixesAlertProps?: ObservableValue<ContractsExpirationService['multipleMixesAlertProps$']>;
  } {
    // don't show any alerts if the feature is disabled
    if (!contractsExpirationFeatureEnabled) {
      return {};
    }

    let contractExpirationAlertProps:
      | ObservableValue<ContractsExpirationService['contractExpirationAlertProps$']>
      | undefined;
    let multipleMixesAlertProps: ObservableValue<ContractsExpirationService['multipleMixesAlertProps$']> | undefined;

    const [pastValue, newValue] = orderCreationFormValues;
    let cwcAlertContractChangedLabel: string | null;

    if (experienceCode === ORDER_EXPERIENCES.COMMAND_UNIVERSAL_LINK && removeContractToggle) {
      cwcAlertContractChangedLabel =
        newValue.expirationContractMoment != null && newValue.formatDate != null
          ? this.translate.instant('views.multiple_orders.template.contract.alert.changed_Contract', {
              expiration: newValue.expirationContractMoment.format(newValue.formatDate),
              contract: newValue.contractSelect,
            })
          : null;
    } else {
      cwcAlertContractChangedLabel =
        newValue.expirationContractMoment != null && newValue.formatDate != null
          ? this.translate.instant('views.multiple_orders.template.contract.alert.changed', {
              expiration: newValue.expirationContractMoment.format(newValue.formatDate),
            })
          : null;
    }

    contractExpirationAlertProps = {
      keepOpen: false,
      show: false,
    };

    if ((newValue.activeStep != null && newValue.activeStep !== 2) || !newValue.contractSelect) {
      contractExpirationAlertProps = {
        keepOpen: false,
        show: false,
      };
    } else if (newValue.expirationContractMoment) {
      const today = moment().add(1, 'hours');

      if (
        newValue.maxDateTimeMoment &&
        newValue.expirationContractMoment.isAfter(today) &&
        newValue.expirationContractMoment.isBefore(newValue.maxDateTimeMoment)
      ) {
        contractExpirationAlertProps = {
          keepOpen: true,
          show: true,
          message: cwcAlertContractChangedLabel,
        };
      }

      if (
        pastValue.selectedDateMoment &&
        pastValue.selectedDateMoment.isSameOrAfter(newValue.expirationContractMoment)
      ) {
        contractExpirationAlertProps = {
          keepOpen: true,
          show: true,
          message: cwcAlertContractChangedLabel,
        };
      }

      if (!!multipleMixesAlertData && multipleMixesAlertData.rmsSpecificationFormValuesLength === 1) {
        multipleMixesAlertProps = {
          keepOpen: false,
          show: false,
        };
      } else if (multipleMixesAlertData && multipleMixesAlertData.rmsSpecificationFormValuesLength > 1) {
        // hide contract expiration alert; multiple mixes takes precedence
        // at least that's what appears to be happening in the logic
        contractExpirationAlertProps = {
          keepOpen: false,
          show: false,
        };
        multipleMixesAlertProps = {
          keepOpen: true,
          show: true,
          message: this.translate.instant('views.multiple_orders.template.order.alert.multiple_mixes'),
        };
      }
    }

    return { contractExpirationAlertProps, multipleMixesAlertProps };
  }
}
